/**
 * /zh-tw/product-discovery — 產品探索 concept page (Traditional Chinese).
 * Links /zh-tw/voice-to-text + /zh-tw/decision-log.
 */
import { MarketingShell } from './MarketingShell';

export function ZhTwProductDiscovery() {
  return (
    <MarketingShell
      currentLocale="zhtw"
      hreflangPath="/product-discovery"
      hreflangLocales={['zhtw']}
      documentTitle="產品探索（Product Discovery）是什麼｜方法與流程 · Draftlize"
      documentDescription="產品探索的方法與流程解析，並教你把使用者訪談的發現，轉成可追蹤的產品決策，而不是流失掉。"
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">指南 · 產品探索</span>
          <h1 className="serif" style={{ fontSize: 'clamp(34px, 4.6vw, 56px)', lineHeight: 1.06, margin: '12px 0 24px' }}>
            產品探索——<br />
            <em>把發現變成決策。</em>
          </h1>
          <p className="lede">
            產品探索（product discovery）是在動手做之前，先確認「該做什麼」的過程。透過使用者訪談、觀察與驗證，把假設換成事實。就算做得好，弱點也常在同一處——好不容易得到的<em>洞察，在變成決策之前就流失了</em>。這頁講產品探索的方法，以及怎麼把發現變成「可追蹤的決策」。
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">產品探索的方法。</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>使用者訪談</strong>——問的是問題與脈絡，不是要對方給答案。</li>
            <li><strong>觀察與行為數據</strong>——看他們做什麼，而不只是說什麼。</li>
            <li><strong>驗證（原型・實驗）</strong>——小規模試假設。</li>
            <li><strong>整合</strong>——把零散發現收斂成足以下決策的模式。</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            訪談先 <a href="/zh-tw/voice-to-text" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>語音轉文字</a> 變成素材。但逐字稿不是終點，而是整合與下決策的起點。
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">弱點在「發現→決策」之間。<em>把它接起來。</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            產品探索的成果，不是訪談裡的一句句話，而是你據此下的<em>決策</em>。很多團隊把發現做成簡報就收工，決策和理由沒留在任何地方。Draftlize 把「發言 → 決策 → 需求」串成一條線。
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">洞察為什麼會流失</h3>
            <p>訪談重點散落在 Notion 頁面或會議記錄裡，下個衝刺就沒人回看，因為決策的「為什麼」和發現被切開了。</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">把「發言 → 決策 → 需求」串起來</h3>
            <p>在 Draftlize，把使用者的一句話連到它促成的決策，再連到由此而來的需求。之後的驗證推翻前提，依賴的東西自動 stale。</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">AI agent 帶著發現往下做</h3>
            <p>探索的決策經 MCP 成為 Claude Code、Cursor 讀取的脈絡。下一份規格能帶著「使用者這樣說、所以這樣決定」往下寫。延伸閱讀 <a href="/zh-tw/decision-log" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>決策紀錄</a>。</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          產品探索的價值，不在你蒐集了多少洞察，而在半年後那些據此下的決策還找不找得回來。
        </blockquote>
        <cite>把發現接到決策，前提變了就提醒你。這就是 Draftlize。</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">常見問題</span>
            <h2 className="serif">FAQ。</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'discovery 和 delivery 差在哪？', a: 'discovery 是確認「該不該做這個」，delivery 是「把它做對」。兩者並行，但探索的決策若沒留下，交付很容易做錯方向。' },
            { q: '要訪談幾位？', a: '比起精確數字，通常做到「不再出現新發現（飽和）」為止。重點不是人數，而是你把每句話連到了哪個決策。' },
            { q: '怎麼整合發現？', a: '把發言依主題收斂，找出足以下決策的模式。在 Draftlize，每個模式都能變成決策卡片，並連回原始發言。' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.9, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">把訪談的一句話，接到決策<em>——免費送 $5。</em></h2>
        </div>
        <div className="trust-strip">
          <span>新註冊送 $5</span>
          <span>用多少付多少</span>
          <span>餘額永不過期</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            把探索的發現，在 Draftlize 變成「發言 → 決策 → 需求」的卡片。前提一變自動 stale，AI agent 也讀得到。
          </p>
          <a className="btn-primary" href="/">免費開始，送 $5</a>
        </div>
      </section>
    </MarketingShell>
  );
}
